import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../lib/api';

// Concept A: aggregated portfolio summary
export const fetchPortfolioSummary = createAsyncThunk(
  'portfolio/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/api/investor/portfolio/summary');
      return data;
    } catch (err) {
      if (err.response?.status === 404) return rejectWithValue('Investor portal not enabled (set ENABLE_INVESTOR_PORTAL=true)');
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Concept B: stress test scenarios + concentration verdict
export const fetchStressTest = createAsyncThunk(
  'portfolio/fetchStress',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/api/financing/portfolio/stress');
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Concept A: real NAV history (daily reconstruction)
export const fetchNavHistory = createAsyncThunk(
  'portfolio/fetchNavHistory',
  async (days = 30, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/api/investor/portfolio/nav-history?days=${days}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Concept C: row-level allocations with computed per-position returns
export const fetchAllocations = createAsyncThunk(
  'portfolio/fetchAllocations',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/api/investor/portfolio/allocations');
      return data?.allocations || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Concept D: wallet (balance + transactions)
//
// The legacy /api/wallets/my-wallet returns the funder's INVESTOR_PROVISION
// wallet, which is NOT the same as the wallet our portfolio scope resolver
// (company:<id>, USD) reads allocations from. Using the provisioning wallet
// here made the Activity tab show a different balance than every other tab,
// confusing the user.
//
// Fix: fetch the summary first, take its wallet_id (already resolved through
// the scope-aware helper on the backend), and load that exact wallet's
// transactions. This way Activity is consistent with Overview / Risk /
// Performance / Stress.
export const fetchWalletWithTransactions = createAsyncThunk(
  'portfolio/fetchWallet',
  async (_, { rejectWithValue }) => {
    try {
      const summaryRes = await api.get('/api/investor/portfolio/summary');
      const walletId = summaryRes.data?.wallet_id;
      if (!walletId) return { wallet: null, transactions: [] };

      const [walletRes, txRes] = await Promise.all([
        api.get(`/api/wallets/my-wallet/${walletId}`).catch(() => null),
        api.get(`/api/wallets/my-wallet/${walletId}/transactions?limit=20`).catch(() => null),
      ]);
      const wallet = walletRes?.data || { id: walletId };
      const transactions = txRes
        ? (Array.isArray(txRes.data) ? txRes.data : (txRes.data?.transactions || []))
        : [];
      return { wallet, transactions };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Concept D / marketplace pulse: open opportunities (cards data, capped)
export const fetchOpportunities = createAsyncThunk(
  'portfolio/fetchOpportunities',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/api/financing/opportunities?limit=12');
      // The endpoint may return either an array or {opportunities:[]} — normalize
      const list = Array.isArray(data) ? data : (data?.opportunities || data?.data || []);
      return list;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);
